import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowBack } from '@mui/icons-material'
import { Autocomplete, Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material'

import { UserLanguage } from '../../../features/account/types/User'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalBrandQuery, useSaveInternalBrandMutation } from '../../api/brands'
import { useGetInternalTagsQuery } from '../../api/tags'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import {
  brandFieldLabelKeys,
  BrandFormValues,
  getBrandTagOptions,
  getUnstoredBrandFields,
  toBrandFormValues,
  toBrandMutation,
} from '../../features/brands/helpers/brandHelpers'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalTag, InternalTagType } from '../../types/InternalTag'
import { brandEditPath } from './BrandsPage'

const listPath = '/internal/brands'
const brandLanguages: UserLanguage[] = ['en', 'ja', 'zh']

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const BrandEditPage: FC = () => {
  const { brandId } = useParams<{ brandId?: string }>()
  usePage(pageService.getInternalPageWithId('internal-brand-edit'))
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const brandQuery = useGetInternalBrandQuery(brandId ?? '', { skip: !brandId })
  const tagsQuery = useGetInternalTagsQuery()
  const [saveBrand, { isLoading: isSaving }] = useSaveInternalBrandMutation()
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<BrandFormValues>({ mode: 'onChange', defaultValues: toBrandFormValues(undefined) })
  const names = useWatch({ control, name: 'names' })
  const { guard, leave } = useEditPageLeave(isDirty)
  const brand = brandId ? brandQuery.data : undefined
  const loadedBrandId = useRef<string>()

  // Only on the first load of a brand, so a refetch after saving can't replace edits that weren't stored.
  useEffect(() => {
    if (brand && loadedBrandId.current !== brand.id) {
      loadedBrandId.current = brand.id
      reset(toBrandFormValues(brand))
    }
  }, [brand, reset])

  const tagOptions = useMemo(() => getBrandTagOptions(tagsQuery.data ?? []), [tagsQuery.data])
  const tagsById = useMemo(() => new Map(tagOptions.map((tag) => [tag.id, tag])), [tagOptions])
  const toTag = useCallback((id: string): InternalTag => tagsById.get(id) ?? { id, name: id, type: InternalTagType.Brand }, [tagsById])

  const handleSave = handleSubmit(async (values) => {
    const body = toBrandMutation(brand, values)
    const saved = await run(saveBrand(body).unwrap(), {
      error: t('internal-analysts:brands_save_failed'),
      errorWithReason: (reason) => t('internal-analysts:brands_save_failed_reason', { reason }),
    })
    if (!saved.ok) return
    const stored = saved.result
    const unstored = getUnstoredBrandFields(body, stored)
    if (unstored.length) {
      const fields = unstored.map((field) => t(brandFieldLabelKeys[field])).join(', ')
      notifyError(t('internal-analysts:brands_not_stored', { fields }))
      if (!brand && stored.id) leave(brandEditPath(stored.id), { replace: true })
      return
    }
    notifySuccess(t('internal-analysts:brands_saved', { name: body.names.en }))
    leave(listPath)
  })

  const isLoading = !!brandId && brandQuery.isLoading
  const isLoadError = !!brandId && brandQuery.isError

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {brandId ? t('internal-analysts:brands_edit') : t('internal-analysts:brands_new')}
          </Typography>
        </Stack>
        <Button variant="contained" onClick={handleSave} disabled={isLoading || isLoadError || isSaving || !isValid}>
          {t('internal-common:save')}
        </Button>
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={isLoadError} errorText={t('internal-analysts:brands_load_one_failed')} skeletonSx={{ maxWidth: 720 }}>
        <Card sx={{ maxWidth: 720 }}>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Controller
                  name="names"
                  control={control}
                  rules={{ validate: (value) => !!value.en?.trim() }}
                  render={({ field }) => (
                    <InputTranslator
                      value={field.value}
                      onChange={field.onChange}
                      label={t('internal-analysts:brands_name')}
                      dialogTitle={t('internal-analysts:brands_name')}
                      selectedLanguages={brandLanguages}
                      disabled={isSaving}
                    />
                  )}
                />
                {!names?.en?.trim() && (
                  <Typography variant="caption" color="error.main">
                    {t('internal-analysts:brands_name_required')}
                  </Typography>
                )}
              </Box>
              <Controller
                name="owner"
                control={control}
                render={({ field }) => <TextField {...field} size="small" label={t('internal-analysts:brands_owner')} disabled={isSaving} />}
              />
              <Controller
                name="tagIds"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    disabled={isSaving}
                    loading={tagsQuery.isLoading}
                    options={tagOptions}
                    value={field.value.map(toTag)}
                    onChange={(_, value) => field.onChange(value.map((tag) => tag.id))}
                    getOptionLabel={(tag) => tag.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) =>
                      value.map((tag, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={tag.id}
                          size="small"
                          label={tag.name}
                          color={tagsById.has(tag.id) || tagsQuery.isLoading ? 'default' : 'warning'}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('internal-analysts:brands_tags')}
                        error={tagsQuery.isError}
                        helperText={tagsQuery.isError ? t('internal-analysts:brands_tags_load_failed') : undefined}
                      />
                    )}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>
      </QueryStateBoundary>

      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:brands_discard_title')}
        text={t('internal-analysts:brands_discard_confirm')}
        discardText={t('internal-analysts:brands_discard')}
      />
    </Box>
  )
}
